import { TradedSidePanel } from 'components/secondaryMarket/live/sidePanel/traded/TradedSidePanel';
import { connect } from 'react-redux';
import type { FormOwnProps } from 'shared/connect/connect';
import { liveFormInputSelectors } from 'state/secondaryMarket/live/form/input/inputForm.selectors';
import { liveSelectors } from 'state/secondaryMarket/live/live.selector';
import {
  getLiveInitialUpfront,
  getLiveQuoteFormattedAccruedInterest,
  getLiveQuotePriceType,
  getLiveQuoteQuoteId,
  getLiveQuoteSalesMarginFormatted,
  getLiveQuoteSettlementDate,
  getLiveQuoteTradeWay,
} from 'state/secondaryMarket/live/quote/liveQuote.selector';
import { secondaryMarketLiveQuoteCancelRequestedThunk } from 'state/secondaryMarket/live/quote/liveQuote.thunk';
import { liveReferentialSelectors } from 'state/secondaryMarket/live/referential/liveReferential.selector';
import { liveTradeSelectors } from 'state/secondaryMarket/live/trade/trade.selector';
import type { SecondaryPriceType, SecondaryQuotationType } from 'state/secondaryMarket/productSearch';
import type { TradeWay } from 'state/secondaryMarket/secondaryMarket.model';
import type { SecondaryMarketFormError } from 'state/secondaryMarket/shared/form/validationResults';
import { getUserPreferencesSecondaryMarketCloseTabWhenOrderPlacedActivated } from 'state/selectors';
import { isSecondaryOrderBlotterVisible } from 'state/selectors/common/router';
import type { ThunkDispatch } from 'state/state.common';
import type { AppState } from 'state/state.model';

type TradedSidePanelStateProps = {
  initialUpfront?: number;
  price: number;
  priceType: SecondaryPriceType;
  priceCurrency: string;
  closeTabWhenLiveTradeCpOrderIsPlacedActivated: boolean;
  validationErrors?: SecondaryMarketFormError[];
  tradeWay: TradeWay;
  settlementDate?: string;
  quoteId?: string;
  upfrontAdjusted?: number;
  salesMargin?: number;
  salesMarginAdjustment?: number;
  tradingPrice?: number;
  tradingPriceAdjustment?: number;
  accruedInterest?: number;
  effectiveCashAmount?: number;
  tradeDate?: string;
  quantity?: number;
  quotationType?: SecondaryQuotationType;
  hasOrderBlotter?: boolean;
};

type TradedSidePanelDispatchProps = {
  onDone: (closeTab?: boolean) => void;
};

type TradedSidePanelOwnProps = FormOwnProps;

export type TradedSidePanelProps = TradedSidePanelDispatchProps & TradedSidePanelOwnProps & TradedSidePanelStateProps;

const mapStateToProps = (state: AppState, { formId }: TradedSidePanelOwnProps): TradedSidePanelStateProps => {
  return {
    price: liveSelectors.getEffectivePriceFormatted(state, formId)!,
    accruedInterest: getLiveQuoteFormattedAccruedInterest(state, formId),
    tradingPrice: liveTradeSelectors.getTradePriceFormatted(state, formId!),
    priceType: (getLiveQuotePriceType(state, formId) ?? liveReferentialSelectors.getPriceType(state, formId))!,
    priceCurrency: liveReferentialSelectors.getCurrency(state, formId)!,
    quoteId: getLiveQuoteQuoteId(state, formId),
    settlementDate: getLiveQuoteSettlementDate(state, formId),
    tradeWay: getLiveQuoteTradeWay(state, formId)!,
    upfrontAdjusted: liveFormInputSelectors.getFormInputCustomizedUpfront(state, formId),
    closeTabWhenLiveTradeCpOrderIsPlacedActivated:
      getUserPreferencesSecondaryMarketCloseTabWhenOrderPlacedActivated(state),
    validationErrors: liveTradeSelectors.getTradeValidationErrors(state, formId),
    salesMargin: getLiveQuoteSalesMarginFormatted(state, formId),
    salesMarginAdjustment: liveFormInputSelectors.getFormInputCustomizedXMarginAdjustment(state, formId),
    tradingPriceAdjustment: liveFormInputSelectors.getFormInputCustomizedTradingPriceAdjustment(state, formId),
    initialUpfront: getLiveInitialUpfront(state, formId),
    effectiveCashAmount: liveTradeSelectors.getCashAmount(state, formId),
    tradeDate: liveTradeSelectors.getTradeTradeDate(state, formId),
    quantity: liveTradeSelectors.getTradeQuantity(state, formId),
    quotationType: liveSelectors.getQuotationType(state, formId!),
    hasOrderBlotter: isSecondaryOrderBlotterVisible(state),
  };
};

const mapDispatchToProps = (
  dispatch: ThunkDispatch,
  { formId: tabId }: TradedSidePanelOwnProps,
): TradedSidePanelDispatchProps => {
  return {
    onDone: closeTab => {
      dispatch(secondaryMarketLiveQuoteCancelRequestedThunk(tabId, closeTab));
    },
  };
};

export const connectTradedSidePanel = connect(mapStateToProps, mapDispatchToProps);

export const ConnectedTradedSidePanel = connectTradedSidePanel(TradedSidePanel);
